import React, {useEffect, useState, useRef, useMemo} from "react";
import {useNavigate} from "react-router-dom";
import {useLazyQuery} from "@apollo/client";

import {
    Box,
    Button,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import {DataGridPro, getGridDateOperators} from "@mui/x-data-grid-pro";
import * as moment from "moment-timezone";
import RefreshIcon from "@mui/icons-material/Refresh";

import {CAP_LEVELS} from "../../domain/CapLevels";
import RefundButton from "./RefundButton";
import {DATETIME_ISO_FORMAT_WITH_SLASHES, DATE_FORMAT} from "../../config/DateTimeConstants";
import {CONTENT_TYPE_ICON_MAP} from "../../domain/ContentTypes";
import ContentTypeIcon from "../sessions/ContentTypeIcon";
import CapLevelChip from "../common/CapLevelChip";
import RecordingCell from "../common/RecordingCell";
import FlagDisplay from "../common/FlagDisplay";
import {LOOKUP_FILTERED_BOOKINGS} from "../../queries/GraphQLQueries";
import {useCurrentUser} from "../../hooks/useCurrentUser";
import EditAssessmentDialog from "./EditAssessmentDialog";
import CancelBookingDialog from "./CancelBookingDialog";
import CustomAlert from "../CustomAlert";
import {buildFilterCriterionFromModel, shouldIncludeInFilter} from "../../helpers/ServerSideFilterUtils";
import ChildTagCell from "./ChildTagCell";

const UNKNOWN_CONTENT_TYPE_INDEX = 0;
const FIRST_CONTENT_INDEX = 0;
const FIRST_PAGE_INDEX = 0;
const PAGE_SIZE_OPTIONS = [25, 50, 100];
const DEFAULT_ROWS_PER_PAGE = PAGE_SIZE_OPTIONS[0];

const RenderToolTip = (params) => {
    return <Tooltip title={params.formattedValue}>
        <Typography variant="body2">{params.formattedValue}</Typography>
    </Tooltip>;
}

const TooltipTableCell = ({params, style}) => {
    return <Tooltip title={params}>
        <TableCell {...style}>{params}</TableCell>
    </Tooltip>;
}

const styles = {
    divider: {
        borderBottom: '0.06rem solid gray',
        width: '100%'
    },
    dataGrid: {
        container: {
            height: 'calc(100vh - 8.5rem)'
        },
        customStyles: {
            "& .MuiDataGrid-cell": {
                display: 'flex',
                alignItems: 'center'
            },
            "& .MuiDataGrid-headerFilterRow": {
                "& .MuiInputBase-input": {
                    padding: '0.5rem 0.5rem'
                }
            }
        }
    }
};

const RenderContent = (params) => {
    return <Stack direction="row" alignItems="center">
        <Stack sx={{marginRight: "-1rem"}}>
            {params.row.contentLevel.map((rowValue, rowIndex) => (
              <React.Fragment key={rowIndex}>
                  {rowIndex > 0 && <Box sx={styles.divider} />}
                  <CapLevelChip capLevel={rowValue} />
              </React.Fragment>
            ))}
        </Stack>
        <Stack>
            <TooltipTableCell params={params.formattedValue.map((rowValue, rowIndex) => (
              <React.Fragment key={rowIndex}>
                  {rowIndex > 0 && <Box sx={styles.divider} />}
                  <Box>{rowValue}</Box>
              </React.Fragment>
            ))}/>
        </Stack>
    </Stack>;
}

const RenderBookingButton = (params) => {

    const navigate = useNavigate();

    const handleBookButtonClick = () => {
        navigate('booking', {
            state: {
                selectedChildExternalId: params.row.childExternalId,
                selectedChildName: params.row.displayNameRomanized,
                contentIds: params.row.contentIds,
                contentName: params.row.contentName,
                currentServiceTypeKeycode: params.row.serviceTypeKey,
                currentServiceType: params.row.serviceType
            }
        });
    }

    return (
        <Button variant="contained" size="small" onClick={handleBookButtonClick}>
            Rebook
        </Button>
    )
}

const RenderStatus = (params) => {
    return <Tooltip title={params.row.reason}>
        <Typography variant="body2">{params.row.status}</Typography>
    </Tooltip>;
}

const RenderContentType = (params) => {
    return <ContentTypeIcon contentType={params.row.contentType}/>;
}

const RenderDate = (params) => {
    return <Tooltip title={moment(params.value).format(DATE_FORMAT)}>
        <Typography variant="body2">{moment(params.value).format(DATETIME_ISO_FORMAT_WITH_SLASHES)}</Typography>
    </Tooltip>;
}

const RenderServiceType = (params) => {
    return <Tooltip title={params.formattedValue}>
        <Typography variant="body2">{params.row.serviceTypeKey}</Typography>
    </Tooltip>;
}

const buildAssessedDisplay = (params) => {
    if (!params.row.isContentAssessable) {
        return null;
    }

    const {isBookingAssessed, isAssessmentPassed, tags} = params.row;

    return <FlagDisplay isChecked={isBookingAssessed} isPassed={isAssessmentPassed} tags={tags}/>;
}

export default function ParentInfoDetailsDisplay({parent, onClearClickedHandler, userTriggeredManualRefresh, handleUserTriggeredManualRefresh}) {

    const DEFAULT_FILTER_OPERATOR = 'OR';
    const DEFAULT_SORT_ORDER = 'ASC';

    const {currentUser} = useCurrentUser();
    const [bookingData, setBookingData] = useState([]);
    const [alertMessage, setAlertMessage] = useState(null);
    const [allowedToManageBookings, setAllowedToManageBookings] = useState(false);
    const [visibleColumns, setVisibleColumns] = useState({id: false, seatId: false, refundedBy: false, editAssessment: false, forceCancelBooking: false});
    const [paginationModel, setPaginationModel] = useState({page: FIRST_PAGE_INDEX, pageSize: DEFAULT_ROWS_PER_PAGE});
    const [sortOrder, setSortOrder] = useState([{field: 'updated', sort: 'DESC'}]);
    const [filterModel, setFilterModel] = useState([]);

    const [lookupParentBookings, {loading: isBookingsLoading, error, data}] = useLazyQuery(LOOKUP_FILTERED_BOOKINGS, {fetchPolicy: 'network-only'});

    const filteredBookingRowCountRef = useRef(data?.filteredBookings.totalBookingsCount || 0);

    useEffect(() => {
        if (error) {
            setAlertMessage({children: error?.message, severity: 'error'});
        }
    }, [error]);

    useEffect(() => {
        if (!parent?.externalId) {
            return;
        }
        
        const filterCriterion = filterModel.items?.length > 0
          ? filterModel.items.filter(shouldIncludeInFilter).map(model => buildFilterCriterionFromModel(model))
          : [];

        lookupParentBookings({variables: {
            externalId: parent?.externalId,
            pageNumber: paginationModel?.page,
            pageSize: paginationModel?.pageSize,
            logicOperator: filterModel?.logicOperator?.toUpperCase() || DEFAULT_FILTER_OPERATOR,
            filterCriterion,
            sortBy: sortOrder?.map(model => model.field).join(),
            sortOrder: sortOrder?.map(model => model.sort).join().toUpperCase() || DEFAULT_SORT_ORDER
        }});

        handleUserTriggeredManualRefresh(false);
    }, [parent?.externalId, paginationModel?.page, paginationModel?.pageSize, sortOrder, filterModel, userTriggeredManualRefresh]);

    useEffect(() => {
        const allowedToEdit = currentUser.canAdmin || currentUser.canTrafficManage;
        setAllowedToManageBookings(allowedToEdit);
    }, [currentUser]);

    const RenderEditButton = (params) => {
        if (!params.row.isContentAssessable) {
            return null;
        }

        return <EditAssessmentDialog bookingId={params.id}/>;
    }

    const RenderCancelButton = (params) => {
        if (!params.row.isForceCancellable) {
            return null;
        }

        return <CancelBookingDialog booking={params.row} setAlertMessage={setAlertMessage}/>;
    }

    const columns = [
        { field: 'id', headerName: 'Booking Id', width: 130, renderCell: RenderToolTip },
        { field: 'displayName', headerName: 'Name', width: 180, renderCell: RenderToolTip },
        { field: 'displayNameRomanized', headerName: 'Latin Name', width: 180, renderCell: RenderToolTip },
        { field: 'serviceType', headerName: 'Service', width: 130, renderCell: RenderServiceType },
        { field: 'contentName', headerName: 'Content', width: 200, renderCell: RenderContent },
        { field: 'contentType', headerName: 'Type', width: 65, align: 'center', headerAlign: 'center', renderCell: RenderContentType, renderHeaderFilter: () => null },
        { field: 'performer', headerName: 'Performer', width: 160, renderCell: RenderToolTip },
        { field: 'seatId', headerName: 'Seat Id', width: 130, renderCell: RenderToolTip },
        { field: 'timeslot', headerName: 'Timeslot', width: 190, renderCell: RenderDate, valueGetter: params => moment(params), filterOperators: getGridDateOperators() },
        { field: 'status', headerName: 'Status', width: 180, renderCell: RenderStatus },
        { field: 'isAssessed', headerName: 'Assessed', width: 80, align: 'center', renderCell: buildAssessedDisplay, filterable: false, sortable: false },
        { field: 'editAssessment', headerName: 'Edit/Assess', width: 100, align: 'center', hideable: allowedToManageBookings, renderCell: RenderEditButton, filterable: false, sortable: false },
        { field: 'recording', headerName: 'Recording', width: 80, renderCell: (params) => <RecordingCell sessionId={params.row?.sessionId} />, filterable: false, sortable: false },
        { field: 'refunded', headerName: 'Refund', width: 100, renderCell: (params) => <RefundButton row={params.row} />, filterable: false, sortable: false },
        { field: 'createBooking', headerName: 'Rebook', width: 100, renderCell: RenderBookingButton, filterable: false, sortable: false },
        { field: 'forceCancelBooking', headerName: 'Cancel', width: 80, align: 'center', hideable: allowedToManageBookings, renderCell: RenderCancelButton, filterable: false, sortable: false },
        { field: 'created', headerName: 'Created', width: 190, renderCell: RenderDate, valueGetter: params => moment(params), filterOperators: getGridDateOperators() },
        { field: 'updated', headerName: 'Updated', width: 190, renderCell: RenderDate, valueGetter: params => moment(params), filterOperators: getGridDateOperators() },
        { field: 'refundedBy', headerName: 'Refunded By', width: 150, renderCell: RenderToolTip }
    ];

    const getMatchingCapLevelValue = (levelKey) => {
        const matchedLevel = CAP_LEVELS.find(level => level.key === levelKey);
        return matchedLevel ? matchedLevel.name : '-';
    }

    const calculateRowHeight = (params) => {
        const defaultRowHeightInPixels = 40;
        const additionalRowHeightPerRowInPixels = 15;

        let rowHeight = defaultRowHeightInPixels;

        const countOfContents = params.model.contentName.length
        if (countOfContents > 1) {
            rowHeight = countOfContents * additionalRowHeightPerRowInPixels + defaultRowHeightInPixels;
        }

        return rowHeight;
    };

    const lookupContentTypeName = (contents) => {
        const firstContent = contents[FIRST_CONTENT_INDEX];
        const filteredContentType = CONTENT_TYPE_ICON_MAP.filter(contents => contents.key === firstContent.contentType);
        let contentType = CONTENT_TYPE_ICON_MAP[UNKNOWN_CONTENT_TYPE_INDEX].name;

        if (filteredContentType.length > 0) {
            contentType = filteredContentType[FIRST_CONTENT_INDEX].name;
        }

        return contentType;
    }

    useEffect(() => {

        if (!data?.filteredBookings) {
            return;
        }

        const bookings = [...data.filteredBookings.bookings]
            .map((booking) => {
                return {
                    'id': booking.id,
                    'childExternalId': booking.child.externalId,
                    'displayName': booking.child.displayName,
                    'displayNameRomanized': booking.child.displayNameRomanized,
                    'contentIds': booking.contents.map(item => item.id),
                    'contentName': booking.contents.map(item => item.name),
                    'serviceTypeKey': booking.serviceType.keycode,
                    'serviceType': booking.serviceType.description,
                    'contentType': lookupContentTypeName(booking.contents),
                    'contentLevel': booking.contents.map(item => getMatchingCapLevelValue(item.level)),
                    'performer': booking.seat?.lesson.performer.givenName || '-',
                    'seatId': booking.seat?.id || '-',
                    'sessionId': booking.seat?.lesson.id,
                    'timeslot': moment(booking.lessonStartTime),
                    'isForceCancellable': booking.isForceCancellable,
                    'status': booking.status,
                    'isContentAssessable':  booking.contents?.some(content => content.isAssessed),
                    'isBookingAssessed': !!booking.assessment?.id,
                    'isAssessmentPassed': booking.assessment?.pass ?? null,
                    'tags': booking.assessment?.tags ?? [],
                    'reason': booking.reasonCode || '-',
                    'eligibleForRefund': booking.eligibleForRefund,
                    'refunded': booking.refunded,
                    'refundedBy': booking.refundedBy,
                    'created': moment(booking.created),
                    'updated': moment(booking.updated),
                };
            });

        setBookingData(bookings);
    }, [data]);

    const rowCount = useMemo(() => {
        if (data?.filteredBookings.totalBookingsCount !== undefined) {
            filteredBookingRowCountRef.current = data?.filteredBookings.totalBookingsCount;
        }
        
        return filteredBookingRowCountRef.current;
    }, [data?.filteredBookings.totalBookingsCount]);

    const handleCloseCustomAlert = () => setAlertMessage(null);

    return <>
        <Box>
            <Paper elevation={4} sx={{marginBottom: "1rem"}} >
                <Stack spacing={2} sx={{p: 2}} alignItems="center" justifyContent="center" direction="row">
                    <TextField id="recordId" label="Id" value={parent?.id || ''} size="small" InputProps={{
                        readOnly: true,
                    }}/>
                    <TextField id="externalId" label="Member Id" value={parent?.externalId || ''} size="small" InputProps={{
                        readOnly: true,
                    }}/>
                    <TextField id="displayName" label="Display Name" value={parent?.displayName || ''} size="small" InputProps={{
                        readOnly: true,
                    }}/>
                    <TextField id="region" label="Region" value={parent?.region || ''} size="small" InputProps={{
                        readOnly: true,
                    }}/>

                    <Button variant="contained" onClick={onClearClickedHandler}>Clear</Button>
                    <Button variant="contained" sx={{minWidth: 'auto', width: '2.3rem'}} onClick={() => handleUserTriggeredManualRefresh(true)}>
                        {<RefreshIcon/>}
                    </Button>
                </Stack>
            </Paper>

            <Paper elevation={4}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Id</TableCell>
                            <TableCell>External Id</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Latin Name</TableCell>
                            <TableCell>Gender</TableCell>
                            <TableCell>Date of Birth</TableCell>
                            <TableCell>CAP Level</TableCell>
                            <TableCell sx={{paddingLeft: "1.8rem"}}>Tags</TableCell>
                            <TableCell align="right">Tokens</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {parent?.children
                            && parent.children.map((child) => (
                                <TableRow key={child.id}>
                                    <TooltipTableCell params={child.id} />
                                    <TooltipTableCell params={child.externalId} />
                                    <TooltipTableCell params={child.displayName} />
                                    <TooltipTableCell params={child.displayNameRomanized} />
                                    <TooltipTableCell params={child.gender} />
                                    <TooltipTableCell params={child.dateOfBirth} />
                                    <TooltipTableCell params={CAP_LEVELS.find(level => level.key === child.capLevel)?.name} />
                                    <ChildTagCell childId={child.id} userTriggeredManualRefresh={userTriggeredManualRefresh} />
                                    <TooltipTableCell params={child.tokenBalance !== null ? child.tokenBalance : "-"} style={{align: 'right'}} />
                                </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>

            <Typography variant="h6" sx={{my: "0.5rem"}}>Bookings</Typography>

            <Paper elevation={4}>
                <DataGridPro
                    headerFilters
                    pagination
                    paginationMode="server"
                    sortingMode="server"
                    filterMode="server"
                    style={styles.dataGrid.container}
                    sx={{...styles.dataGrid.customStyles, "& .MuiDataGrid-footerContainer": {minHeight: 40, maxHeight: 40}}}
                    rows={bookingData}
                    columns={columns}
                    loading={isBookingsLoading}
                    getRowHeight={calculateRowHeight}
                    columnVisibilityModel={visibleColumns}
                    initialState={{
                        sorting: { sortModel: [{field: 'updated', sort: 'desc'}] }
                    }}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    onColumnVisibilityModelChange={newModel => setVisibleColumns(newModel)}
                    rowCount={rowCount}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    onSortModelChange={setSortOrder}
                    onFilterModelChange={setFilterModel}
                    slotProps={{
                      headerFilterCell: {
                        showClearIcon: true,
                      }
                    }}
                />
            </Paper>
        </Box>
        <CustomAlert alertMessage={alertMessage} handleCloseCustomAlert={handleCloseCustomAlert}/>
    </>;

}